import { SpaceValues } from "@/lib/utils/SpaceValues";
import { StatisticsByCustomerThreeYearsStatisticsDetailsQuery } from "@/gql/graphql";
import { LineChart, LineChartOptions, ScaleTypes } from "@carbon/charts-react";
import { Box, Row, Space } from "@stenajs-webui/core";
import { Banner, Card, CardBody, CardHeader } from "@stenajs-webui/elements";
import { StandardTable } from "@stenajs-webui/grid";
import * as React from "react";
import { threeYearsPricePerMeterTableConfig } from "../config/ThreeYearsPricePerMeterTableConfig";
import { transformThreeYearsPricePerMeterToTableRows } from "../lib/transformers/customer-table.transformer";

interface Props {
  year: number;
  currencyCode: string;
  threeYearsPricePerMeter: StatisticsByCustomerThreeYearsStatisticsDetailsQuery["statistics"]["customerDetails"]["threeYearsPricePerMeterByCustNoYearAndCurrency"];
  loading: boolean;
  customerId: string;
}

export const ThreeYearsPricePerMeterTable: React.FC<Props> = ({
  year,
  currencyCode,
  threeYearsPricePerMeter,
  loading,
  customerId,
}) => {
  const options: LineChartOptions = {
    tooltip: {
      customHTML: (_tooltipModel: any, defaultTooltipHTML: string) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(defaultTooltipHTML, "text/html");

        const tooltips = doc.querySelectorAll(".datapoint-tooltip");

        tooltips.forEach((tooltipEl) => {
          const labelEl = tooltipEl.querySelector(".label p");
          const valueEl = tooltipEl.querySelector(".value");

          if (
            labelEl &&
            valueEl &&
            valueEl.textContent &&
            labelEl.textContent
          ) {
            const label = labelEl.textContent.trim();

            // Only add currencyCode for route rows or when label is y-value
            const isYValue = label.toLowerCase() === "y-value";
            const isRouteLabel = !["group", "x-value", "year"].includes(
              label.toLowerCase()
            );

            if (isYValue || isRouteLabel) {
              valueEl.textContent += ` ${currencyCode}`;
            }

            // Optionally replace known labels
            if (label.toLowerCase() === "group") {
              labelEl.textContent = "Route";
            } else if (label.toLowerCase() === "x-value") {
              labelEl.textContent = "Year";
            } else if (label.toLowerCase() === "y-value") {
              labelEl.textContent = "Price";
            }
          }
        });

        return doc.body.innerHTML;
      },
      showTotal: false,
    },

    legend: {
      enabled: true,
      position: "bottom",
      alignment: "center",
    },
    fileDownload: {
      fileName: `price-per-meter-${customerId}-${currencyCode}`,
    },
    tabularRepModal: {
      tableHeadingFormatter(headings) {
        return headings.map((heading) => {
          if (heading === "Group") {
            return "Route";
          }
          if (heading === "x-value") {
            return "Year";
          }
          if (heading === "y-value") {
            return `Price (${currencyCode})`;
          }
          return heading;
        });
      },
    },

    axes: {
      bottom: {
        mapsTo: "key",
        scaleType: ScaleTypes.LABELS,
      },
      left: {
        mapsTo: "value",
        scaleType: ScaleTypes.LINEAR,
        ticks: {
          formatter: (tick: number | Date) =>
            typeof tick === "number"
              ? `${tick} ${currencyCode}`
              : tick.toString(),
        },
      },
    },
    height: "500px",
  };

  const formattedData = transformThreeYearsPricePerMeterToTableRows(
    threeYearsPricePerMeter,
    year
  )
    .flatMap((item) => {
      const points = [
        { year: item.year - 2, value: item.pricePerMeterMinus2Year },
        { year: item.year - 1, value: item.pricePerMeterMinus1Year },
        { year: item.year, value: item.pricePerMeter },
      ].filter((p) => typeof p.value === "number" && p.value > 0);

      return points.map((p) => ({
        group: item.routePairCode,
        key: p.year.toString(),
        value: p.value,
      }));
    })
    .sort((a, b) => Number(a.key) - Number(b.key));

  return (
    <Row width={"calc(100% - 48px)"}>
      <Card width={"100%"} alignSelf={"flex-start"}>
        <CardHeader text={"Price per meter"} />
        <CardBody width={"100%"}>
          <StandardTable
            items={transformThreeYearsPricePerMeterToTableRows(
              threeYearsPricePerMeter,
              year
            ).sort((a, b) => a.routePairCode.localeCompare(b.routePairCode))}
            loading={loading}
            config={threeYearsPricePerMeterTableConfig(year, currencyCode)}
          />
        </CardBody>
      </Card>
      <Space num={SpaceValues.TWENTYFOUR} />
      <Card width={"100%"} alignSelf={"flex-start"}>
        <CardHeader text={"Price per meter"} />
        <Box width={"100%"} indent={SpaceValues.TWENTYFOUR}>
          {formattedData.length > 0 ? (
            <LineChart data={formattedData} options={options} />
          ) : (
            <Banner
              headerText="Missing statistics"
              text="Select another year to show statistics"
              variant="info"
            />
          )}
        </Box>
        <Space num={SpaceValues.TWENTYFOUR} />
      </Card>
    </Row>
  );
};
